import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ClockIcon } from '@heroicons/react/20/solid';
import { DayTimeSlotList } from './DayTimeSlotList';
import { ScrollWheel } from './ScrollWheel';
import {
    DAY_TIME_SLOTS,
    formatDisplayTime,
    from24HourString,
    HOURS_12,
    MINUTES_30,
    PERIODS,
    snapToDayTimeSlot,
    snapToNearestSlot,
    to24HourString,
} from './timePickerUtils';
import './TimePickerModern.css';
import { usePickerPanelPosition } from './usePickerPanelPosition';

/**
 * @typedef {object} TimePickerModernProps
 * @property {string} [value] - Hora en formato 24h "HH:MM"
 * @property {(value: string) => void} onChange
 * @property {string} [label]
 * @property {string} [placeholder]
 * @property {boolean} [disabled]
 * @property {string} [className]
 * @property {string|null} [error]
 * @property {boolean} [required]
 * @property {boolean} [daySlots] - Solo slots de día completo cada 30 min (00:00–23:59)
 */

const defaultParts = () => {
    const snapped = snapToNearestSlot('08:00');
    return from24HourString(snapped.time);
};

/**
 * @param {TimePickerModernProps} props
 */
export const TimePickerModern = ({
    value = '',
    onChange,
    label,
    placeholder = 'Seleccionar hora',
    disabled = false,
    className = '',
    error = null,
    required = false,
    daySlots = false,
}) => {
    const generatedId = useId();
    const pickerId = `time-picker-${generatedId}`;
    const containerRef = useRef(null);
    const fieldRef = useRef(null);
    const panelRef = useRef(null);

    const [isOpen, setIsOpen] = useState(false);
    const { placement, compact, position: panelPosition } = usePickerPanelPosition(
        fieldRef,
        panelRef,
        isOpen,
    );
    const [draft, setDraft] = useState(defaultParts);

    const committedDaySlot = useMemo(() => {
        if (!value) return '08:00';
        return snapToDayTimeSlot(value);
    }, [value]);

    const committedParts = useMemo(() => {
        if (!value) return defaultParts();
        return from24HourString(snapToNearestSlot(value).time);
    }, [value]);

    const displayParts = isOpen ? draft : committedParts;

    const closePicker = useCallback(() => {
        setIsOpen(false);
        if (!daySlots) {
            setDraft(committedParts);
        }
    }, [committedParts, daySlots]);

    const openPicker = useCallback(() => {
        if (disabled) return;
        if (!daySlots) {
            setDraft(committedParts);
        }
        setIsOpen(true);
    }, [committedParts, daySlots, disabled]);

    const handleDaySlotSelect = useCallback((slot) => {
        onChange(snapToDayTimeSlot(slot));
        setIsOpen(false);
    }, [onChange]);

    const handleDone = () => {
        const time24 = to24HourString(draft.hour, draft.minute, draft.period);
        const snapped = snapToNearestSlot(time24);
        onChange(snapped.time);
        setIsOpen(false);
    };
    useEffect(() => {
        if (!isOpen) return undefined;

        const handlePointerDown = (event) => {
            if (
                containerRef.current?.contains(event.target)
                || panelRef.current?.contains(event.target)
            ) {
                return;
            }
            closePicker();
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, [closePicker, isOpen]);

    const triggerLabel = value
        ? (daySlots ? committedDaySlot : formatDisplayTime(committedParts))
        : placeholder;

    const isPanelVisible = isOpen && Boolean(panelPosition);
    const panelNode = (
        <div
            ref={panelRef}
            className="time-picker__panel time-picker__panel--portal"
            role="dialog"
            aria-label={label || 'Seleccionar hora'}
            data-open={isPanelVisible ? 'true' : 'false'}
            data-placement={placement}
            data-compact={compact}
            data-day-slots={daySlots ? 'true' : 'false'}
            style={{
                position: 'fixed',
                zIndex: 99999,
                visibility: isPanelVisible ? 'visible' : 'hidden',
                pointerEvents: isPanelVisible ? 'auto' : 'none',
                left: panelPosition?.left,
                width: panelPosition?.width,
                maxHeight: panelPosition?.maxHeight,
                ...(panelPosition?.top != null
                    ? { top: panelPosition.top, bottom: 'auto' }
                    : { bottom: panelPosition?.bottom, top: 'auto' }),
            }}
        >
            <div className="time-picker__panel-body">
                <div className="time-picker__display">
                    <span className="time-picker__display-icon" aria-hidden="true">
                        <ClockIcon />
                    </span>
                    <span
                        key={daySlots
                            ? committedDaySlot
                            : `${displayParts.hour}-${displayParts.minute}-${displayParts.period}`}
                        className="time-picker__display-value"
                    >
                        {daySlots ? committedDaySlot : formatDisplayTime(displayParts)}
                    </span>
                    {!daySlots ? (
                        <span className="time-picker__period-badge">
                            {displayParts.period}
                        </span>
                    ) : null}
                </div>

                {daySlots ? (
                    <DayTimeSlotList
                        slots={DAY_TIME_SLOTS}
                        value={committedDaySlot}
                        onSelect={handleDaySlotSelect}
                    />
                ) : (
                    <div className="time-picker__wheels-wrap">
                        <div className="time-picker__highlight" aria-hidden="true" />
                        <div className="time-picker__wheels">
                            <ScrollWheel
                                items={HOURS_12}
                                value={draft.hour}
                                onChange={(hour) => setDraft((prev) => ({ ...prev, hour }))}
                            />
                            <ScrollWheel
                                items={MINUTES_30}
                                value={draft.minute}
                                onChange={(minute) => setDraft((prev) => ({ ...prev, minute }))}
                            />
                            <div className="time-picker__period-toggle" role="group" aria-label="Periodo">
                                {PERIODS.map((period) => (
                                    <button
                                        key={period}
                                        type="button"
                                        className="time-picker__period-btn"
                                        data-active={draft.period === period}
                                        aria-pressed={draft.period === period}
                                        onClick={() => setDraft((prev) => ({ ...prev, period }))}
                                    >
                                        {period}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {!daySlots ? (
                <div className="time-picker__actions">
                    <button
                        type="button"
                        className="time-picker__btn time-picker__btn--ghost"
                        onClick={closePicker}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="time-picker__btn time-picker__btn--primary"
                        onClick={handleDone}
                    >
                        Listo
                    </button>
                </div>
            ) : null}
        </div>
    );

    return (
        <div
            ref={containerRef}
            className={`time-picker ${className}`.trim()}
        >
            {label ? (
                <label
                    htmlFor={pickerId}
                    id={`${pickerId}-label`}
                    className="time-picker__label"
                >
                    {label}
                    {required ? (
                        <span className="time-picker__required" aria-hidden="true">*</span>
                    ) : null}
                </label>
            ) : null}

            <div
                ref={fieldRef}
                className="time-picker__field"
                data-open={isOpen}
                data-disabled={disabled}
                data-error={error ? 'true' : 'false'}
            >
                <div className="time-picker__icon-box" aria-hidden="true">
                    <ClockIcon />
                </div>

                <button
                    id={pickerId}
                    type="button"
                    className="time-picker__trigger"
                    disabled={disabled}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    aria-labelledby={label ? `${pickerId}-label` : undefined}
                    aria-label={label ? undefined : placeholder}
                    onClick={() => {
                        if (isOpen) {
                            closePicker();
                        } else {
                            openPicker();
                        }
                    }}
                >
                    <span
                        className={`time-picker__trigger-value ${
                            value ? '' : 'time-picker__trigger-placeholder'
                        }`}
                    >
                        {triggerLabel}
                    </span>
                    {value && !daySlots ? (
                        <span className="time-picker__period-badge">
                            {committedParts.period}
                        </span>
                    ) : null}
                </button>
            </div>

            {error ? (
                <p className="time-picker__error" role="alert">
                    {error}
                </p>
            ) : null}

            {isOpen && typeof document !== 'undefined'
                ? createPortal(panelNode, document.body)
                : null}
        </div>
    );
};
